import {
  Music2,
  Guitar,
  SlidersHorizontal,
  AudioWaveform,
  Activity,
  BarChart3,
  LogIn,
  Disc3,
  Mic2,
  Sparkles,
  Spline,
  Waves,
  Zap,
  TrendingUp,
  CircleStop,
  LogOut,
  type LucideProps,
} from 'lucide-react';
import { type ComponentType } from 'react';
import { Section, SectionType, getSectionLabel } from '../utils/sectionClassifier';
import { PanningVisualizer } from './PanningVisualizer';
import { EQMap } from './EQMap';
import { SampleExtractor } from './SampleExtractor';
import './SectionInsights.css';

const SECTION_ICONS: Record<SectionType, ComponentType<LucideProps>> = {
  'mix-in':    LogIn,
  'intro':     Disc3,
  'verse':     Mic2,
  'chorus':    Sparkles,
  'bridge':    Spline,
  'breakdown': Waves,
  'main-drop': Zap,
  'build':     TrendingUp,
  'outro':     CircleStop,
  'mix-out':   LogOut,
};

const SECTION_TONES: Record<SectionType, string> = {
  'mix-in':    'var(--bk-text-2)',
  'intro':     'var(--bk-section-hero)',
  'verse':     'var(--bk-text)',
  'chorus':    'var(--bk-section-problems)',
  'bridge':    'var(--bk-section-budget)',
  'breakdown': 'var(--bk-section-editorial)',
  'main-drop': 'var(--bk-accent)',
  'build':     'var(--bk-section-live)',
  'outro':     'var(--bk-text-2)',
  'mix-out':   'var(--bk-text-muted)',
};

/* ── Derived placeholders (used until backend supplies frequencySpectrum
   and per-instrument pan). Drives section-to-section variation so the
   visualizers don't look static. ──────────────────────────────────── */

function panForType(type: string, sectionType: SectionType): number {
  const t = type.toLowerCase();
  if (t.includes('kick') || t.includes('drum') || t.includes('bass')) return 0;
  if (t.includes('lead') || t.includes('vocal')) return 0;
  if (t.includes('pad')) return sectionType === 'main-drop' ? -0.7 : -0.45;
  if (t.includes('hi-hat') || t.includes('cymbal')) return sectionType === 'main-drop' ? 0.55 : 0.3;
  if (t.includes('guitar')) return -0.3;
  if (t.includes('piano')) return 0.25;
  if (t.includes('synth')) return sectionType === 'main-drop' ? 0.4 : -0.25;
  if (t.includes('fx')) return sectionType === 'breakdown' ? 0.85 : 0.6;
  return 0;
}

function derivePannedInstruments(section: Section) {
  if (!section.instruments) return [];
  return section.instruments.map((inst) => ({
    type: inst.type,
    pan: panForType(inst.type, section.type),
  }));
}

function derivePanning(section: Section) {
  // Width grows with energy: drops feel wide, intros narrow.
  const energy = Math.max(0.1, Math.min(1, section.avgEnergy));
  const wide = section.type === 'main-drop' || section.type === 'chorus' || section.type === 'build';
  const narrow = section.type === 'intro' || section.type === 'breakdown' || section.type === 'mix-in';
  let left = 0.30 + (wide ? 0.18 : narrow ? -0.05 : 0) + energy * 0.15;
  let right = left;
  let center = Math.max(0.20, 1 - left - right);
  // Re-normalize to sum 1 in case of jitter.
  const sum = left + right + center;
  return { left: left / sum, right: right / sum, center: center / sum };
}

interface FreqPoint { frequency: number; amplitude: number }

function resolveSpectrum(section: Section): FreqPoint[] {
  // Use real backend data if it exists AND is non-empty.
  const real = section.segments[0]?.mixing?.frequencySpectrum;
  if (real && typeof real === 'object') {
    const entries = Object.entries(real)
      .map(([f, a]) => ({
        frequency: parseFloat(f),
        amplitude: typeof a === 'number' ? a : 0,
      }))
      .filter((p) => Number.isFinite(p.frequency));
    if (entries.length > 0) return entries;
  }
  return synthesizeSpectrum(section);
}

function synthesizeSpectrum(section: Section): FreqPoint[] {
  // Build a smooth EQ curve from the instrument list. Each instrument
  // contributes a hump centered at its frequency range, weighted by its
  // confidence. The result is a per-section spectrum that reflects the
  // detected ensemble, even when the backend hasn't returned a real one.
  const points = [
    20, 30, 50, 80, 120, 200, 350, 500, 800, 1200,
    2000, 3000, 5000, 8000, 12000, 16000, 20000,
  ];
  const energy = Math.max(0.1, Math.min(1, section.avgEnergy || 0.5));
  const isDrop = section.type === 'main-drop' || section.type === 'chorus';
  const isIntro = section.type === 'intro' || section.type === 'mix-in';

  const humps: Array<{ center: number; spread: number; weight: number }> = [];
  for (const inst of section.instruments || []) {
    const t = inst.type.toLowerCase();
    let center = 1000;
    let spread = 1.4; // log-octaves
    if (t.includes('kick') || t.includes('drum')) { center = 60;   spread = 0.7; }
    else if (t.includes('bass'))                  { center = 130;  spread = 0.9; }
    else if (t.includes('vocal'))                 { center = 1200; spread = 1.2; }
    else if (t.includes('lead') || t.includes('synth')) { center = 1800; spread = 1.6; }
    else if (t.includes('pad'))                   { center = 700;  spread = 2.0; }
    else if (t.includes('hi-hat') || t.includes('cymbal')) { center = 8000; spread = 0.8; }
    else if (t.includes('guitar'))                { center = 900;  spread = 1.4; }
    else if (t.includes('piano'))                 { center = 700;  spread = 1.6; }
    else if (t.includes('fx'))                    { center = 4000; spread = 1.8; }
    humps.push({ center, spread, weight: Math.max(0.3, inst.confidence || 0.6) });
  }

  return points.map((f) => {
    let amp = 0;
    const lf = Math.log10(f);
    for (const h of humps) {
      const lc = Math.log10(h.center);
      const d = (lf - lc) / h.spread;
      amp += h.weight * Math.exp(-d * d);
    }
    // Bias toward energy: drops boost lows + highs, intros taper highs.
    if (isDrop && (f < 250 || f > 3000)) amp *= 1.25;
    if (isIntro && f > 4000) amp *= 0.6;
    amp *= 0.55 + 0.45 * energy;
    return { frequency: f, amplitude: Math.max(0.05, Math.min(1, amp)) };
  });
}

interface SectionInsightsProps {
  section: Section;
  sectionIndex: number;
  totalSections: number;
  analysisId?: string;
}

export function SectionInsights({ section, sectionIndex, totalSections, analysisId }: SectionInsightsProps) {
  const duration = section.endTime - section.startTime;
  const durationFormatted = `${Math.floor(duration / 60)}:${Math.floor(duration % 60).toString().padStart(2, '0')}`;

  // Music theory insights
  const getChordProgressionInsight = (chords: string[]): string => {
    if (!chords || chords.length === 0) return 'No chord progression detected';

    // Simple analysis
    if (chords.length >= 4) {
      return `4-chord progression: ${chords.join(' → ')}. Common in ${section.type === 'main-drop' ? 'main drop' : section.type} sections.`;
    }
    return `Chord progression: ${chords.join(' → ')}`;
  };

  const getKeyInsight = (key?: string): string => {
    if (!key) return 'Key not detected';
    return `Tonal center: ${key}. ${section.dominantKey === key ? 'Consistent throughout section.' : 'May modulate.'}`;
  };

  const getInstrumentInsight = (instruments?: Array<{ type: string; confidence: number }>): string => {
    if (!instruments || instruments.length === 0) return 'No instruments detected';
    
    // Map to common instrument names
    const instrumentMap: Record<string, string> = {
      'drum': 'Kick',
      'kick': 'Kick',
      'snare': 'Snare',
      'hihat': 'Hi-Hat',
      'hi-hat': 'Hi-Hat',
      'cymbal': 'Hi-Hat',
      'bass': 'Bass',
      'synth': 'Synth',
      'synthesizer': 'Synth',
      'lead': 'Lead Synth',
      'lead synth': 'Lead Synth',
      'pad': 'Pads',
      'pads': 'Pads',
      'vocal': 'Vocals',
      'vocals': 'Vocals',
      'voice': 'Vocals',
      'fx': 'FX',
      'effects': 'FX',
      'guitar': 'Guitar',
      'piano': 'Piano',
    };

    const mapped = instruments.map(inst => {
      const lower = inst.type.toLowerCase();
      const mappedName = Object.keys(instrumentMap).find(key => lower.includes(key));
      return mappedName ? instrumentMap[mappedName] : inst.type;
    });

    // Remove duplicates and format
    const unique = [...new Set(mapped)];
    return unique.join(', ');
  };

  // Section-aware synth character — combines instrument confidence + the
  // section's spectral profile to make the hint actually informative
  // instead of always "saw wave".
  const getSynthAnalysis = (instruments?: Array<{ type: string; confidence: number }>): string => {
    if (!instruments) return '';
    const synth = instruments.find(
      (i) =>
        i.type.toLowerCase().includes('synth') ||
        i.type.toLowerCase().includes('lead'),
    );
    if (!synth) return '';

    const spec = section.segments[0]?.mixing?.frequencySpectrum || {};
    const bands = Object.values(spec) as number[];
    const total = bands.reduce((a, b) => a + (b || 0), 0);
    const norm = (v: number) => (total > 0 ? v / total : 0);
    const high =
      norm((spec as any).high || (spec as any).high_freq || 0) +
      norm((spec as any).treble || 0);
    const lowMid =
      norm((spec as any).low_mid || (spec as any).lowMid || 0) +
      norm((spec as any).bass || (spec as any).low || 0);

    const isDrop =
      section.type === 'main-drop' ||
      section.type === 'chorus' ||
      section.type === 'build';
    const isPad = section.type === 'breakdown' || section.type === 'intro';

    if (isPad && lowMid > 0.4) {
      return 'Lush pad sound — long attack, slow filter sweep. Massive or Diva-style territory.';
    }
    if (isDrop && high > 0.35) {
      return 'Bright lead with strong upper-harmonic content — saw or supersaw, classic Serum / Sylenth1 character.';
    }
    if (synth.confidence > 0.7) {
      return 'Pluck or arp character — short attack, harmonic-rich. FM / wavetable synthesis.';
    }
    return 'Synth presence detected — wavetable-style harmonic character.';
  };

  // Drum-kit summary derived from instrument detector + section context.
  // The worker only outputs a single "drums" tag today, so this is
  // best-effort: it reports presence + intensity and infers which kit
  // pieces are likely driving based on the section archetype.
  const getDrumKitInsight = ():
    | { line: string; pieces: { name: string; active: boolean }[] }
    | null => {
    const drums = section.instruments?.find((i) =>
      /drum|kick|snare|hihat|hi-hat|cymbal|clap/i.test(i.type),
    );
    if (!drums) return null;

    const conf = drums.confidence;
    const heavy = section.type === 'main-drop' || section.type === 'chorus';
    const sparse = section.type === 'breakdown' || section.type === 'intro';
    const pieces = [
      { name: 'Kick', active: !sparse || conf > 0.55 },
      { name: 'Snare / Clap', active: !sparse || conf > 0.6 },
      { name: 'Hi-hats', active: heavy || conf > 0.5 },
      { name: 'Percussion / FX', active: heavy && conf > 0.65 },
    ];
    let line: string;
    if (heavy && conf > 0.7) {
      line = `Full driving kit — kick on every beat, hats on offbeats. Detection confidence ${(conf * 100).toFixed(0)}%.`;
    } else if (sparse && conf < 0.5) {
      line = `Drums dropping out — minimal percussion to build tension. Detection confidence ${(conf * 100).toFixed(0)}%.`;
    } else if (heavy) {
      line = `Solid kit driving the section. Detection confidence ${(conf * 100).toFixed(0)}%.`;
    } else {
      line = `Drum elements present but restrained. Detection confidence ${(conf * 100).toFixed(0)}%.`;
    }
    return { line, pieces };
  };

  // Vocals: lead, sample, processed, or instrumental.
  const getVocalInsight = (): string => {
    const vocal = section.instruments?.find((i) =>
      /vocal|voice|vox/i.test(i.type),
    );
    if (!vocal) return 'Instrumental — no vocal content detected in this section.';
    const conf = vocal.confidence;
    if (conf > 0.7) return `Lead vocal present (${(conf * 100).toFixed(0)}% confidence). Likely the main melodic / lyrical focus of the section.`;
    if (conf > 0.45) return `Vocal sample or chops (${(conf * 100).toFixed(0)}% confidence). Rhythmic or atmospheric vocal use.`;
    return `Faint vocal presence (${(conf * 100).toFixed(0)}% confidence). Possibly heavily processed or background only.`;
  };

  // Mix character — describe compression, dynamic range, and EQ tilt as
  // single-sentence prose using whatever the worker provided.
  const getMixCharacter = (): string | null => {
    const mix = section.segments[0]?.mixing;
    if (!mix) return null;
    const parts: string[] = [];

    if (typeof mix.compressionRatio === 'number') {
      if (mix.compressionRatio > 0.7) parts.push('heavily compressed');
      else if (mix.compressionRatio > 0.45) parts.push('moderately glued');
      else parts.push('open, lightly compressed');
    }
    if (typeof mix.dynamicRange === 'number') {
      if (mix.dynamicRange < 6) parts.push('tight dynamic range');
      else if (mix.dynamicRange > 12) parts.push('wide dynamic range');
    }

    const spec = mix.frequencySpectrum || {};
    const bass =
      ((spec as any).low || 0) + ((spec as any).low_mid || 0);
    const treble =
      ((spec as any).high || 0) + ((spec as any).high_mid || 0);
    if (bass > 0 && treble > 0) {
      const ratio = bass / (bass + treble);
      if (ratio > 0.65) parts.push('bass-forward');
      else if (ratio < 0.35) parts.push('bright / treble-tilted');
      else parts.push('balanced spectrum');
    }
    if (parts.length === 0) return null;
    return parts.join(' · ');
  };

  const getEnergyInsight = (): string => {
    // Floor at 5% so an intro never reads as a literal "0%".
    const energyPct = Math.max(5, Math.round(section.avgEnergy * 100));
    const level = section.avgEnergy > 0.7
      ? 'High'
      : section.avgEnergy < 0.4
      ? 'Low'
      : 'Moderate';

    // Section-type-aware copy — the same energy reading means different
    // things depending on where in the track you are.
    const typeNarratives: Partial<Record<string, string>> = {
      'intro':     `${level} energy (${energyPct}%). Sets the mood and introduces the sonic palette before the first drop.`,
      'mix-in':    `${level} energy (${energyPct}%). Quiet entry — the DJ-friendly window for blending in from the previous track.`,
      'breakdown': `${level} energy (${energyPct}%). Tension build by stripping rhythm — the calm before the next drop.`,
      'main-drop': `${level} energy (${energyPct}%). Peak moment — full kick, bass, and lead all firing.`,
      'chorus':    `${level} energy (${energyPct}%). Hook section, repeated for memorability.`,
      'build':     `${level} energy (${energyPct}%). Rising intensity — riser, snare roll, and filter sweeps push toward the drop.`,
      'verse':     `${level} energy (${energyPct}%). Narrative section, lower than the chorus to create dynamic contrast.`,
      'bridge':    `${level} energy (${energyPct}%). One-time variation that breaks repetition before the final chorus / drop.`,
      'outro':     `${level} energy (${energyPct}%). Closing material — the mix-out window for the next DJ.`,
      'mix-out':   `${level} energy (${energyPct}%). Tail of the track — kick fades, leaving room for the next mix.`,
    };

    return (
      typeNarratives[section.type] ||
      `${level} energy (${energyPct}%). Maintains flow.`
    );
  };

  const getSectionRole = (): string => {
    const roles: Record<string, string> = {
      intro: 'Sets the mood and introduces musical themes. Typically establishes key and tempo.',
      verse: 'Develops the narrative. Usually lower energy than chorus, allows for vocal storytelling.',
      chorus: 'The hook and most memorable part. Highest energy, repeated throughout the track.',
      bridge: 'Provides contrast and prevents repetition. Often modulates key or changes harmonic direction.',
      outro: 'Brings the track to conclusion. May fade out or end with a strong cadence.',
      drop: 'The climax in EDM. Maximum energy, heavy bass, creates the "drop" moment.',
      breakdown: 'Moment of tension release. Minimal instrumentation, builds anticipation.',
      build: 'Energy increases toward the drop. Creates anticipation through rising intensity.',
    };
    return roles[section.type] || 'Musical section with specific role in track structure.';
  };

  const SectionIcon = SECTION_ICONS[section.type];
  const tone = SECTION_TONES[section.type] || 'var(--bk-section-hero)';
  const sectionLabel = getSectionLabel(section);

  return (
    <div className="section-insights-card" style={{ ['--section-tone' as any]: tone }}>
      <div className="section-insights-header">
        <div className="section-title-row">
          <div className="section-name-wrap">
            {SectionIcon && (
              <span className="section-name-icon">
                <SectionIcon size={16} strokeWidth={1.8} />
              </span>
            )}
            <h3 className="section-name">{sectionLabel}</h3>
          </div>
          <span className="section-timestamp">{section.timestamp}</span>
        </div>
        <div className="section-duration">{durationFormatted} duration</div>
      </div>

      <div className="section-role">
        <span className="section-role-eyebrow">Musical Role</span>
        <p className="section-role-text">{getSectionRole()}</p>
      </div>

      <div className="insights-grid">
        <div className="insight-item">
          <div className="insight-label">
            <Music2 size={12} strokeWidth={2} />
            <span>Harmonic Analysis</span>
          </div>
          <div className="insight-content">
            <p><span className="insight-key">Key</span>{getKeyInsight(section.dominantKey)}</p>
            <p><span className="insight-key">Chord Progression</span>{getChordProgressionInsight(section.chordProgression || [])}</p>
          </div>
        </div>

        <div className="insight-item">
          <div className="insight-label">
            <Guitar size={12} strokeWidth={2} />
            <span>Instrumentation</span>
          </div>
          <div className="insight-content">
            <p><span className="insight-key">Instruments</span>{getInstrumentInsight(section.instruments)}</p>
            {getSynthAnalysis(section.instruments) && (
              <p className="synth-analysis">{getSynthAnalysis(section.instruments)}</p>
            )}
            {section.instruments && section.instruments.length > 0 && (
              <div className="instrument-list">
                {section.instruments.map((inst, idx) => (
                  <span key={idx} className="instrument-tag">
                    {inst.type} <em>{(inst.confidence * 100).toFixed(0)}%</em>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Drum kit — surfaced separately so DJs can see at a glance whether
            the kit is full, restrained, or dropped out for this section. */}
        {(() => {
          const drum = getDrumKitInsight();
          if (!drum) return null;
          return (
            <div className="insight-item">
              <div className="insight-label">
                <Activity size={12} strokeWidth={2} />
                <span>Drum Kit</span>
              </div>
              <div className="insight-content">
                <p>{drum.line}</p>
                <div className="kit-pieces">
                  {drum.pieces.map((p) => (
                    <span
                      key={p.name}
                      className={`kit-piece ${p.active ? 'is-active' : 'is-quiet'}`}
                    >
                      {p.name}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          );
        })()}

        {/* Vocals — lead / sample / instrumental classification. */}
        <div className="insight-item">
          <div className="insight-label">
            <Mic2 size={12} strokeWidth={2} />
            <span>Vocals</span>
          </div>
          <div className="insight-content">
            <p>{getVocalInsight()}</p>
          </div>
        </div>

        {/* Mix character — compression, dynamic range, EQ tilt prose. */}
        {(() => {
          const character = getMixCharacter();
          if (!character) return null;
          return (
            <div className="insight-item">
              <div className="insight-label">
                <SlidersHorizontal size={12} strokeWidth={2} />
                <span>Mix Character</span>
              </div>
              <div className="insight-content">
                <p>{character}</p>
              </div>
            </div>
          );
        })()}

        <div className="insight-item full-width">
          <div className="insight-label">
            <SlidersHorizontal size={12} strokeWidth={2} />
            <span>Panning &amp; Spatial Positioning</span>
          </div>
          <div className="insight-content">
            <PanningVisualizer
              panning={derivePanning(section)}
              instruments={derivePannedInstruments(section)}
            />
            <p className="panning-note">
              Leads, kick, and bass are centered. Pads and other elements are panned L/R for width.
            </p>
          </div>
        </div>

        <div className="insight-item full-width">
          <div className="insight-label">
            <AudioWaveform size={12} strokeWidth={2} />
            <span>Frequency Spectrum (EQ)</span>
          </div>
          <div className="insight-content">
            <EQMap
              frequencies={resolveSpectrum(section)}
              instruments={section.instruments?.map(inst => {
                const type = inst.type.toLowerCase();
                let range = { min: 100, max: 1000 };
                if (type.includes('kick') || type.includes('drum')) range = { min: 20, max: 100 };
                else if (type.includes('bass')) range = { min: 60, max: 250 };
                else if (type.includes('synth') || type.includes('lead')) range = { min: 200, max: 4000 };
                else if (type.includes('pad')) range = { min: 100, max: 2000 };
                else if (type.includes('hi-hat') || type.includes('cymbal')) range = { min: 5000, max: 15000 };
                else if (type.includes('vocal')) range = { min: 200, max: 4000 };
                return { type: inst.type, frequencyRange: range };
              })}
            />
          </div>
        </div>

        <div className="insight-item">
          <div className="insight-label">
            <Activity size={12} strokeWidth={2} />
            <span>Energy Profile</span>
          </div>
          <div className="insight-content">
            <p>{getEnergyInsight()}</p>
            <div className="energy-bar">
              <div
                className="energy-fill"
                style={{ width: `${Math.max(5, Math.round(section.avgEnergy * 100))}%` }}
              />
            </div>
          </div>
        </div>

        <div className="insight-item">
          <div className="insight-label">
            <BarChart3 size={12} strokeWidth={2} />
            <span>Section Stats</span>
          </div>
          <div className="insight-content section-stats">
            <div className="stat-row">
              <span className="stat-label">Position</span>
              <span className="stat-value">{sectionIndex + 1} / {totalSections}</span>
            </div>
            <div className="stat-row">
              <span className="stat-label">Segments</span>
              <span className="stat-value">{section.segments.length}</span>
            </div>
            <div className="stat-row">
              <span className="stat-label">Confidence</span>
              <span className="stat-value">{(section.confidence * 100).toFixed(0)}%</span>
            </div>
          </div>
        </div>

        {analysisId && (
          <div className="sample-extraction-section full-width">
            <SampleExtractor
              analysisId={analysisId}
              startTime={section.startTime}
              endTime={section.endTime}
              sectionType={section.type}
            />
          </div>
        )}
      </div>
    </div>
  );
}

